/**
 * AI CONTEXT: security - Amplify initialization with Cognito Auth only
 * Journal: J-20250809-01
 * 
 * Purpose:
 * - Initialize Amplify Auth using safe client-side identifiers
 * - Validate security configuration at runtime
 * - Log service readiness without exposing secrets
 * 
 * Future considerations:
 * - Add API and Storage categories when backend is ready
 * - Use environment-driven configuration per environment
 * - Integrate error reporting on config failures
 */

import { Amplify } from 'aws-amplify';
import { awsConfig as cognitoConfig, validateSecurityConfig } from '../config';

// Security validation
const isSecure = validateSecurityConfig();

if (!isSecure) {
  console.error('Security validation failed - AWS services may not work correctly');
}

// Check if AWS services are configured
const hasAwsServices = cognitoConfig.userPoolId && 
                      cognitoConfig.userPoolClientId;

if (process.env.NODE_ENV !== 'production') {
  console.log('AWS Services Status:', {
    environment: process.env.NODE_ENV,
    hasUserPool: !!cognitoConfig.userPoolId,
    hasClientId: !!cognitoConfig.userPoolClientId,
    hasAwsServices,
    securityValidated: isSecure,
    userPoolConfigured: !!cognitoConfig.userPoolId,
    clientConfigured: !!cognitoConfig.userPoolClientId,
    userPoolId: cognitoConfig.userPoolId,
    clientId: cognitoConfig.userPoolClientId,
    region: cognitoConfig.region
  });
}

// Only configure Amplify if we have the required services and security is valid
if (hasAwsServices && isSecure) {
  // AWS Amplify v6 configuration with actual services
  const amplifyConfig = {
    // Auth configuration for Cognito
    Auth: {
      Cognito: {
        userPoolId: cognitoConfig.userPoolId,
        userPoolClientId: cognitoConfig.userPoolClientId,
        region: cognitoConfig.region,
        // Identity pool only needed for AWS service access (S3, etc.)
        // Not required for API Gateway JWT authentication
        ...(process.env.REACT_APP_IDENTITY_POOL_ID
          ? { identityPoolId: process.env.REACT_APP_IDENTITY_POOL_ID }
          : {}),
        loginWith: {
          email: true,
          phone: false,
          username: false
        }
      }
    },
    
    // API configuration for REST API Gateway
    API: {
      REST: {
        [process.env.REACT_APP_API_NAME || 'TolbertGiveawaysApi']: {
          endpoint: process.env.REACT_APP_API_ENDPOINT || 'https://fvtiy379r0.execute-api.us-east-2.amazonaws.com/prod',
          region: cognitoConfig.region,
        }
      }
    },
    
    // Storage configuration for S3 public assets (product images)
    // Only configure if identity pool is available
    ...(process.env.REACT_APP_S3_BUCKET && process.env.REACT_APP_IDENTITY_POOL_ID ? {
      Storage: {
        S3: {
          bucket: process.env.REACT_APP_S3_BUCKET,
          region: cognitoConfig.region,
        }
      }
    } : {})
  };

  try {
    // AI CONTEXT: deployment - Initialize Amplify only when config is valid
    // Reasoning: Avoid runtime failures in production; keep logs minimal
    // Constraints: Client must not expose secrets; only public IDs allowed
    if (process.env.NODE_ENV !== 'production') {
      console.log('Initializing AWS Amplify with real services...');
      console.log('Security validation passed - configuring AWS services');
    }
    Amplify.configure(amplifyConfig);
    console.log('AWS Amplify configured successfully with real services');
  } catch (error) {
    console.error('Failed to configure AWS Amplify:', error);
  }
} else {
  console.log('AWS services not configured or security validation failed - running in mock mode');
  console.log('The app will work with mock data until AWS services are properly configured');
  
  // Don't configure Amplify if we don't have the services or security is invalid
  // This prevents the app from crashing when AWS services aren't available
}

const awsConfig = {
  isConfigured: hasAwsServices && isSecure,
  region: cognitoConfig.region,
  securityValidated: isSecure,
  hasIdentityPool: !!process.env.REACT_APP_IDENTITY_POOL_ID,
  hasS3Bucket: !!process.env.REACT_APP_S3_BUCKET,
};

if (process.env.NODE_ENV !== 'production') {
  // Helpful consolidated log to diagnose missing envs in client builds
  console.log('Amplify env (sanitized):', {
    isConfigured: awsConfig.isConfigured,
    region: awsConfig.region,
    hasIdentityPool: awsConfig.hasIdentityPool,
    hasS3Bucket: awsConfig.hasS3Bucket,
    userPoolConfigured: !!cognitoConfig.userPoolId,
    clientConfigured: !!cognitoConfig.userPoolClientId,
  });
}

export default awsConfig; 